import { useCallback, useEffect, useRef, useState } from 'react';
import { listAudioVersions } from '@/services/audioService';

// The API signs narration URLs for an hour. One that fails sooner than this after it was read is broken, not expired,
// so it is not read again (a fresh signature for a missing file would fail the same way, forever).
const FRESH_FAILURE_MS = 30_000;
const LOAD_FAILED = 'Failed to load audio';

interface Source {
  key: string;
  src: string | null;
  readAt: number;
  error: string | null;
}

export interface NarrationAudioSrc {
  src: string | null;
  loading: boolean;
  error: string | null;
  /** Wire to the <audio> element's onError: an expired URL is read again once, anything else shows the error. */
  onError: () => void;
}

async function readAudioUrl(slideId: string, version: number | null | undefined, signal: AbortSignal): Promise<string | null> {
  const versions = await listAudioVersions(slideId, { signal });
  const take = versions.find((v) => (version == null ? v.isCurrentVersion : v.version === version));
  return take?.status === 'COMPLETED' ? take.audioUrl : null;
}

/**
 * The presigned S3 URL a slide's narration plays from (`version` omitted = the current take). `known` is a URL the
 * caller has just read with the slide's audio; it is used as is for that slide and version, so nothing is read twice.
 * Pass a null slideId to read nothing.
 */
export function useNarrationAudioSrc(slideId: string | null | undefined, version?: number | null, known?: string | null): NarrationAudioSrc {
  const key = slideId ? `${slideId}:${version ?? 'current'}` : null;
  const [source, setSource] = useState<Source | null>(null);
  const [reread, setReread] = useState<{ key: string } | null>(null);
  const knownRef = useRef(known);
  knownRef.current = known;

  useEffect(() => {
    if (!key || !slideId) return;
    const rereading = reread?.key === key;
    if (!rereading && knownRef.current) {
      setSource({ key, src: knownRef.current, readAt: Date.now(), error: null });
      return;
    }
    setSource({ key, src: null, readAt: Date.now(), error: null });
    const ctrl = new AbortController();
    readAudioUrl(slideId, version, ctrl.signal).then(
      (src) => { if (!ctrl.signal.aborted) setSource({ key, src, readAt: Date.now(), error: src ? null : LOAD_FAILED }); },
      () => { if (!ctrl.signal.aborted) setSource({ key, src: null, readAt: Date.now(), error: LOAD_FAILED }); },
    );
    return () => ctrl.abort();
  }, [key, slideId, version, reread]);

  const current = key && source?.key === key ? source : null;

  const onError = useCallback(() => {
    if (!current?.src) return;
    if (Date.now() - current.readAt < FRESH_FAILURE_MS) setSource({ ...current, error: LOAD_FAILED });
    else setReread({ key: current.key });
  }, [current]);

  return {
    src: current?.src ?? null,
    loading: !!key && !(current?.src || current?.error),
    error: current?.error ?? null,
    onError,
  };
}
